import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Flex, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import clsx from "clsx";
import {
  IconArrowLeft,
  IconArrowRight,
  IconMicrophone,
} from "@tabler/icons-react";
import { MessageList } from "../MessageList";
import { MessageInput } from "../MessageInput";
import {
  NOT_ACCEPTING_CANDIDATES_MESSAGE,
  PausedScreen,
} from "../PausedScreen";
import { YesNo } from "../YesNo";
import { MultipleChoice } from "../MultipleChoice";
import { FileUpload } from "../FileUpload";
import { CompletionBadge } from "../CompletionBadge";
import { InterviewFeedback } from "../InterviewFeedback";
import { Voice, getAllowTextFallback, getCanSwitchToVoice } from "../Voice";
import { SKIP_RESPONSE_VALUE } from "../../constants/voice";
import { AnalyticsEvents } from "../../constants/analytics-events";
import {
  useConfetti,
  useInterview,
  VoiceEditUnsupportedError,
} from "../../hooks";
import {
  deriveResponseFormat,
  type EditTargetState,
} from "../../hooks/useInterview";
import { useTrackAnalytics } from "../../hooks/useAnalytics";
import {
  computeResponseEditMetadata,
  translateIdentity,
  type StaticTextTranslator,
} from "../../utils";
import {
  InterviewPhase,
  InterviewUIStatus,
  InterviewMessageResponseFormat,
  InterviewQuestionInputType,
  InputOption,
  Message,
  MessageRole,
  ResponseFormat,
} from "../../types";
import styles from "./ChatContainer.module.css";

interface ChatContainerProps {
  interviewLocationId: string;
  candidateId?: string;
  /** Company name displayed on messages */
  company?: string;
  /** Resume mode: existing session ID to reconnect to */
  resumeSessionId?: string;
  /** Resume mode: pre-loaded messages from previous session */
  resumeMessages?: Message[];
  /** Resume mode: Pusher channel from resume session */
  resumePusherChannel?: string;
  /** Resume mode: phase to restore from checkpoint */
  resumePhase?: InterviewPhase;
  /** Resume mode: server-enforced edit capability */
  resumeCanEditPreviousResponse?: boolean;
  /** Called when session ID is available (for parent components like Header) */
  onSessionReady?: (sessionId: string) => void;
  /** Called when candidate email is detected from user messages */
  onCandidateEmailReady?: (email: string) => void;
  /** Called when a multilingual session language becomes known */
  onLanguageReady?: (languageCode: string) => void;
  /**
   * AS-1564: lifts the candidate-facing progress fields up to the parent
   * (typically `InterviewPageContent`) so the Header can render the
   * ProgressBar. Either value can be undefined — undefined means "no
   * progress data yet" (pre-T&C) or "cleared on session reset".
   */
  onProgressUpdate?: (
    currentQuestionNumber: number | undefined,
    totalQuestions: number | undefined,
  ) => void;
  /** Optional translation function for static UI text */
  translate?: StaticTextTranslator;
}

const getLatestPriorQuestionAnswer = (
  messages: Message[],
): Message | undefined => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (
      message?.role === MessageRole.USER &&
      message.questionId &&
      message.questionType !== "intro" &&
      message.questionType !== "conclusion"
    ) {
      return message;
    }
  }

  return undefined;
};

const isEditablePriorQuestionAnswer = (message: Message): boolean =>
  message.inputType !== InterviewQuestionInputType.FILE_UPLOAD &&
  message.inputType !== InterviewQuestionInputType.VOICE &&
  message.responseFormat !== ResponseFormat.VoiceRecommended &&
  message.responseFormat !== ResponseFormat.VoiceRequired &&
  message.answerResponseFormat !== "voice" &&
  !message.audioS3Key;

const findEditableDisplayTargetIndex = (
  messages: Message[],
  editTargetMessageId: string,
): number =>
  messages.findIndex(
    (message) =>
      message.id === editTargetMessageId && message.role === MessageRole.USER,
  );

const getEditDisplayMessages = (
  messages: Message[],
  editTarget: EditTargetState,
): Message[] => {
  const targetIndex = findEditableDisplayTargetIndex(
    messages,
    editTarget.messageId,
  );
  if (targetIndex < 0) {
    return messages;
  }

  const nextMessages = messages.slice(0, targetIndex);
  if (!editTarget.prompt.trim()) {
    return nextMessages;
  }

  for (let index = nextMessages.length - 1; index >= 0; index -= 1) {
    if (nextMessages[index]?.role === MessageRole.ASSISTANT) {
      return nextMessages.map((message, messageIndex) =>
        messageIndex === index
          ? { ...message, content: editTarget.prompt }
          : message,
      );
    }
  }

  return nextMessages;
};

const getLanguageCodeFromOption = (option: InputOption): string | null =>
  option.optionValue ?? option.id ?? null;

/** Timeout thresholds in seconds */
const TIMEOUT_WARNING_SECONDS = 10;
const TIMEOUT_ERROR_SECONDS = 20;

export function ChatContainer({
  interviewLocationId,
  candidateId,
  company = "Your Company",
  resumeSessionId,
  resumeMessages,
  resumePusherChannel,
  resumePhase,
  resumeCanEditPreviousResponse,
  onSessionReady,
  onCandidateEmailReady,
  onLanguageReady,
  onProgressUpdate,
  translate = translateIdentity,
}: ChatContainerProps) {
  const {
    messages,
    isConnected,
    isLoading,
    phase,
    status,
    sessionId,
    usersTurn,
    questionInputType,
    currentQuestionId,
    questionOptions,
    voiceUrls,
    responseFormat,
    canSkip,
    recordingDurationSeconds,
    setQuestionInputType,
    timeSinceLastMessage,
    cancelReason,
    notAcceptingCandidates,
    currentQuestionNumber,
    totalQuestions,
    canEditPreviousResponse,
    isEditingPreviousResponse,
    editTarget,
    isBackLoading,
    markProgressViewFired,
    declineIntroduction,
    sendMessage,
    requestPreviousQuestionEdit,
    submitPreviousQuestionEdit,
  } = useInterview({
    interviewLocationId,
    candidateId,
    onError: (error) => {
      console.error("Interview error:", error);
    },
    onLanguageChange: onLanguageReady,
    resumeSessionId,
    resumeMessages,
    resumePusherChannel,
    resumePhase,
    resumeCanEditPreviousResponse,
  });
  const track = useTrackAnalytics({ interviewLocationId });
  const startedSessionIdsRef = useRef<Set<string>>(new Set());

  // Notify parent when session ID is available
  useEffect(() => {
    if (sessionId && onSessionReady) {
      onSessionReady(sessionId);
    }
  }, [sessionId, onSessionReady]);

  // AS-1564: lift progress fields up so the Header can render the ProgressBar.
  // Fires whenever the hook's progress values change (including resume
  // rehydration on mount). undefined means "no progress yet" or "session
  // reset cleared the bar".
  useEffect(() => {
    if (onProgressUpdate) {
      onProgressUpdate(currentQuestionNumber, totalQuestions);
    }
  }, [currentQuestionNumber, totalQuestions, onProgressUpdate]);

  // Detect candidate email from user messages
  useEffect(() => {
    if (!onCandidateEmailReady) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const userMessages = messages.filter((m) => m.role === MessageRole.USER);
    const emailMsg = userMessages
      .filter((m: Message) => emailRegex.test(m.content.trim()))
      .pop();
    if (emailMsg) {
      onCandidateEmailReady(emailMsg.content.trim());
    }
  }, [messages, onCandidateEmailReady]);

  const handleSend = useCallback(
    (
      content: string,
      options?: {
        selectedOptionIds?: string[];
        declineValue?: string;
        displayContent?: string;
        inputType?: InterviewQuestionInputType;
        audioS3Key?: string;
        responseFormat?: InterviewMessageResponseFormat;
      },
    ) => {
      // Single choke point for the back-request freeze: text/file/choice
      // inputs are visually disabled while isBackLoading, but surfaces
      // without a disabled prop (Voice recorder, Skip) still route here — a
      // forward send racing the server's edit-context creation would
      // conflict interview state, so drop it.
      if (isBackLoading) {
        return Promise.reject(
          new Error("Interview is loading the previous question"),
        );
      }

      const declineValue = options?.declineValue ?? content;
      if (
        phase === InterviewPhase.INTRODUCTION &&
        declineValue.trim().toLowerCase() === "no"
      ) {
        declineIntroduction();
        return Promise.resolve();
      }

      // Match the v2 funnel boundary: introduction declines return before
      // Start Screening. All other screenings start on the first non-edit
      // response attempt, before the backend accepts or rejects it.
      if (
        sessionId &&
        !isEditingPreviousResponse &&
        !startedSessionIdsRef.current.has(sessionId)
      ) {
        startedSessionIdsRef.current.add(sessionId);
        track(AnalyticsEvents.StartScreening, {
          interviewLocationId,
          sessionId,
        });
      }

      const submit = isEditingPreviousResponse
        ? submitPreviousQuestionEdit
        : sendMessage;

      // YTICS-1796: capture privacy-safe edit metadata (length + char
      // deltas + Levenshtein) before `submit()` runs.
      // `submitPreviousQuestionEdit` clears `editTarget` on success, so
      // reading it in the `.then()` would yield `null`.
      //
      // Snapshots use the RAW `content` submitted to the backend, not
      // the translated `displayContent` — for choice questions in a
      // localized interview `displayContent` is the translated label
      // ("Sí") while `editTarget.content` is the stored raw option text
      // ("Yes"); comparing across levels would misreport localized
      // resubmits as changed.
      //
      // Snapshots are also trim-normalized so a candidate resubmitting
      // the stored answer verbatim doesn't report a change over a
      // trailing-whitespace difference — `MessageInput.handleSend`
      // trims outgoing text, but `editTarget.content` comes straight
      // from the server and can carry incidental whitespace.
      //
      // Raw content is intentionally NOT sent to Segment — candidate
      // answers can contain PII and Segment forwards to Braze /
      // warehouse / other destinations. `computeResponseEditMetadata`
      // reduces the strings to privacy-safe integer signals that still
      // let analysts distinguish no-op resubmits from real edits and
      // classify tweak vs substantive rewrite.
      const editMetadata = isEditingPreviousResponse
        ? computeResponseEditMetadata(
            editTarget?.content?.trim() ?? null,
            content.trim(),
          )
        : null;

      return submit(content, {
        displayContent: options?.displayContent,
        inputType: options?.inputType,
        selectedOptionIds: options?.selectedOptionIds,
        audioS3Key: options?.audioS3Key,
        responseFormat: options?.responseFormat,
      }).then(() => {
        if (isEditingPreviousResponse && editMetadata) {
          track(AnalyticsEvents.PreviousQuestionEditSubmitted, {
            currentQuestionId,
            sessionId,
            responseFormat: deriveResponseFormat(options),
            ...editMetadata,
          });
        }
      });
    },
    [
      currentQuestionId,
      declineIntroduction,
      editTarget,
      interviewLocationId,
      isBackLoading,
      isEditingPreviousResponse,
      phase,
      sendMessage,
      sessionId,
      submitPreviousQuestionEdit,
      track,
    ],
  );

  // Fire-and-forget wrapper for callers that don't await handleSend.
  // sendMessage already surfaces errors via the onError callback; this catch
  // exists solely to prevent unhandled promise rejections.
  const handleSendFireAndForget = useCallback(
    (
      content: string,
      options?: {
        selectedOptionIds?: string[];
        declineValue?: string;
        displayContent?: string;
        inputType?: InterviewQuestionInputType;
        responseFormat?: InterviewMessageResponseFormat;
      },
    ) => {
      handleSend(content, options).catch(() => {
        // Already surfaced via onError; swallow to avoid unhandled rejection.
      });
    },
    [handleSend],
  );

  const handleChoiceSubmit = useCallback(
    (selected: InputOption | InputOption[]) => {
      if (Array.isArray(selected)) {
        const content = selected.map((opt) => opt.optionText).join(", ");
        const displayContent = selected
          .map((opt) => translate(opt.optionText))
          .join(", ");
        handleSendFireAndForget(content, {
          selectedOptionIds: selected.map((opt) => opt.id),
          displayContent,
        });
        return;
      }

      handleSendFireAndForget(selected.optionText, {
        selectedOptionIds: [selected.id],
        declineValue: selected.optionValue ?? selected.optionText,
        displayContent: translate(selected.optionText),
      });
    },
    [handleSendFireAndForget, translate],
  );

  const handleChoiceSelectionChange = useCallback(
    (selected: InputOption[]) => {
      if (phase !== InterviewPhase.LANGUAGE_SELECTION) {
        return;
      }

      const selectedOption = selected[0];
      if (!selectedOption) {
        return;
      }

      const languageCode = getLanguageCodeFromOption(selectedOption);
      if (languageCode) {
        onLanguageReady?.(languageCode);
      }
    },
    [onLanguageReady, phase],
  );

  const isCompleted = status === InterviewUIStatus.COMPLETED;

  // Check if interview is completed/cancelled
  const isInterviewEnded =
    isCompleted || status === InterviewUIStatus.CANCELLED;

  useConfetti(isCompleted);

  // Fire `Screener Timeout` once when the inactivity timer crosses the
  // error threshold. Mirrors v2 screener_frontend's chat-container behavior
  // — a single funnel event per session, guarded by a ref so subsequent
  // ticks don't re-fire. Uses the same `timeSinceLastMessage` state the
  // UI already watches for the timeout warning/error copy.
  //
  // Also short-circuits once the interview has ended (COMPLETED /
  // CANCELLED) so a stalled timer past completion can't emit a spurious
  // timeout on a session that finished cleanly.
  const timeoutTrackSentRef = useRef(false);
  useEffect(() => {
    if (
      timeoutTrackSentRef.current ||
      isInterviewEnded ||
      timeSinceLastMessage < TIMEOUT_ERROR_SECONDS
    ) {
      return;
    }
    timeoutTrackSentRef.current = true;
    track(AnalyticsEvents.ScreenerTimeout);
  }, [isInterviewEnded, timeSinceLastMessage, track]);

  // AS-1832: fire `Complete Screening` once when the candidate's UI
  // transitions into COMPLETED. The `hasFiredComplete` guard prevents
  // re-firing on subsequent renders if status stays COMPLETED (e.g.
  // confetti toggling state).
  const [hasFiredComplete, setHasFiredComplete] = useState(false);
  // AS-1866: candidate feedback is offered once at completion; hide the form
  // and show a confirmation badge after it's submitted.
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  useEffect(() => {
    if (isCompleted && !hasFiredComplete) {
      track(AnalyticsEvents.CompleteScreening);
      setHasFiredComplete(true);
    }
  }, [hasFiredComplete, isCompleted, track]);

  // AS-1564: fire `Progress Indicator Viewed` once per session on first
  // visible render of the progress bar. The dedupe ref lives on the
  // `useInterview` hook (see `markProgressViewFired`) so it survives
  // ChatContainer / Header remounts (StrictMode double-mount, route
  // transitions, language-change remounts). PRD §14.1 payload.
  useEffect(() => {
    if (
      !sessionId ||
      currentQuestionNumber == null ||
      totalQuestions == null ||
      totalQuestions <= 0
    ) {
      return;
    }
    if (!markProgressViewFired(sessionId)) {
      return;
    }
    track(AnalyticsEvents.ProgressIndicatorViewed, {
      currentQuestionNumber,
      totalQuestions,
      surface: "interview",
      deviceType:
        typeof window !== "undefined" &&
        window.matchMedia?.("(max-width: 480px)").matches
          ? "mobile"
          : "desktop",
      sessionId,
      interviewLocationId,
    });
  }, [
    sessionId,
    currentQuestionNumber,
    totalQuestions,
    markProgressViewFired,
    track,
    interviewLocationId,
  ]);

  // Resets the Voice recorder component to its initial state on discard.
  const [voiceComponentKey, setVoiceComponentKey] = useState(0);

  // Tracks whether a voice recording is currently uploading. Used to disable
  // the Skip button so a candidate can't dispatch SKIP_RESPONSE_VALUE while
  // the S3 PUT + onSubmit are still in flight.
  const [isVoiceUploading, setIsVoiceUploading] = useState(false);

  // AS-1850: mirror the voice race-protection for file uploads — prevents
  // dispatching SKIP_RESPONSE_VALUE while the S3 PUT + onSubmit are inflight.
  const [isFileUploading, setIsFileUploading] = useState(false);

  const handleBackToPreviousQuestion = useCallback(() => {
    track(AnalyticsEvents.PreviousQuestionClicked, {
      currentQuestionId,
      sessionId,
    });
    requestPreviousQuestionEdit()
      .then((target) => {
        // Attribute the event to the question actually reopened — the
        // closure's currentQuestionId is the question being LEFT, and may
        // already be stale by the time the request resolves.
        track(AnalyticsEvents.PreviousQuestionEdited, {
          currentQuestionId: target?.questionId ?? null,
          sessionId,
        });
      })
      .catch((error: unknown) => {
        if (error instanceof VoiceEditUnsupportedError) {
          // Media editing is deferred (no Figma flow yet) — friendly
          // notice, tracked as an abandon.
          notifications.show({
            message: translate("Editing this answer is not available yet"),
          });
          track(AnalyticsEvents.PreviousQuestionEditAbandoned, {
            currentQuestionId,
            sessionId,
            reason: "unsupported_media",
          });
          return;
        }
        // Other errors already surfaced by the hook.
      });
  }, [
    currentQuestionId,
    requestPreviousQuestionEdit,
    sessionId,
    track,
    translate,
  ]);

  // AS-1565 abandon analytics: candidate leaves mid-edit (refresh/close).
  // pagehide is the most reliable terminal hook on mobile Safari.
  useEffect(() => {
    if (!isEditingPreviousResponse) {
      return;
    }
    const onPageHide = () => {
      track(AnalyticsEvents.PreviousQuestionEditAbandoned, {
        currentQuestionId,
        sessionId,
        reason: "page_exit",
      });
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [isEditingPreviousResponse, track, currentQuestionId, sessionId]);
  const latestPriorQuestionAnswer = getLatestPriorQuestionAnswer(messages);
  const displayMessages =
    isEditingPreviousResponse && usersTurn && editTarget
      ? getEditDisplayMessages(messages, editTarget)
      : messages;
  const hasPriorQuestionAnswer = !!latestPriorQuestionAnswer;
  const hasEditablePriorQuestionAnswer =
    !!latestPriorQuestionAnswer &&
    isEditablePriorQuestionAnswer(latestPriorQuestionAnswer);
  const isBeforeFirstQuestionAnswer =
    !hasPriorQuestionAnswer && currentQuestionNumber === 1;
  // Figma: the back link is PERSISTENT in the footer once editing is
  // possible — it renders disabled (grey) rather than disappearing while
  // already editing (one step back only) or while any submission is in
  // flight. It only unmounts when the capability itself is absent or the
  // interview has ended. Do not gate on progress: assessment follow-ups can
  // still be editable while currentQuestionNumber equals totalQuestions.
  // hasEditablePriorQuestionAnswer OR already-editing: entering edit mode on
  // the very first question removes the only user bubble — the link must stay
  // mounted (disabled), not vanish (Figma: persistent). Before the first
  // question answer exists, render the control disabled per product direction.
  const isPreviousQuestionVisible =
    canEditPreviousResponse &&
    (hasEditablePriorQuestionAnswer ||
      isBeforeFirstQuestionAnswer ||
      isEditingPreviousResponse) &&
    status === InterviewUIStatus.IN_PROGRESS;
  const isPreviousQuestionDisabled =
    isEditingPreviousResponse ||
    !hasEditablePriorQuestionAnswer ||
    !usersTurn ||
    isLoading ||
    isBackLoading ||
    isVoiceUploading ||
    isFileUploading;

  // Whether choice inputs should be interactive
  const isChoiceEnabled =
    usersTurn && status === InterviewUIStatus.IN_PROGRESS && !isLoading;

  // Check if current question is a choice type (Yes/No, Single, Multiple)
  const isChoiceQuestion =
    questionInputType === InterviewQuestionInputType.YES_NO ||
    questionInputType === InterviewQuestionInputType.SINGLE_CHOICE ||
    questionInputType === InterviewQuestionInputType.MULTIPLE_CHOICE;

  // Render the appropriate input component based on question type
  const renderInputComponent = () => {
    // For choice questions, show "Select answer above" when user's turn
    if (isChoiceQuestion && isChoiceEnabled) {
      return (
        <Flex
          align="center"
          className={styles.hintTextContainer}
          justify="center"
        >
          <Text>{translate("Select answer above")}</Text>
        </Flex>
      );
    }

    // For non-choice questions or when not user's turn
    if (!usersTurn && !isInterviewEnded) {
      const hintText = !isConnected
        ? translate("Reconnecting to your interview...")
        : timeSinceLastMessage >= TIMEOUT_ERROR_SECONDS
          ? translate(
              "The screener is currently experiencing issues. Please return later to complete your screening.",
            )
          : timeSinceLastMessage >= TIMEOUT_WARNING_SECONDS
            ? translate("Hang in there, still working on a response...")
            : translate("Waiting for next question...");

      return (
        <Flex
          align="center"
          className={styles.hintTextContainer}
          justify="center"
        >
          <Text
            size={timeSinceLastMessage >= TIMEOUT_WARNING_SECONDS ? "sm" : "md"}
          >
            {hintText}
          </Text>
        </Flex>
      );
    }

    // While voiceUrls are absent we MUST NOT auto-downgrade to text:
    // `responseFormat` may be `undefined`, which makes `getAllowTextFallback`
    // return true, so a voice_required question would silently render a text
    // input. Show the "preparing" placeholder instead — the candidate can
    // still use the Skip button (rendered separately below) if `canSkip` is
    // set.
    if (questionInputType === InterviewQuestionInputType.VOICE) {
      if (!voiceUrls || isBackLoading) {
        return (
          <Flex
            align="center"
            className={styles.hintTextContainer}
            justify="center"
          >
            <Text>
              {translate(
                isBackLoading
                  ? "Loading previous question..."
                  : "Preparing voice recorder...",
              )}
            </Text>
          </Flex>
        );
      }

      return (
        <Voice
          key={voiceComponentKey}
          canSkip={canSkip}
          maxDurationSeconds={recordingDurationSeconds}
          onReset={() => setVoiceComponentKey((k) => k + 1)}
          onSubmit={async (audioKey) => {
            // Only emit the analytics event once the backend has accepted the
            // response — a network error after the S3 upload would otherwise
            // record a submission Segment never received.
            // AS-1844: pass `audioS3Key` + explicit `responseFormat: 'voice'`
            // so `interview_session_message.audio_s3_key` gets populated and
            // downstream surfaces (PDF competency PLAY RECORDING, TalentHub
            // side-panel) can resolve the recording.
            await handleSend(audioKey, {
              audioS3Key: audioKey,
              responseFormat: "voice",
              inputType: InterviewQuestionInputType.VOICE,
            });
            track(AnalyticsEvents.VoiceResponseSubmitted, {
              currentQuestionId,
              responseFormat,
            });
          }}
          onSwitchToText={
            getAllowTextFallback(responseFormat)
              ? () => {
                  track(AnalyticsEvents.VoiceModeSwitchedToText, {
                    currentQuestionId,
                    responseFormat,
                  });
                  setQuestionInputType(InterviewQuestionInputType.TEXT);
                }
              : undefined
          }
          onUploadingChange={setIsVoiceUploading}
          resetCount={voiceComponentKey}
          translate={translate}
          voiceUrls={voiceUrls}
        />
      );
    }

    // Show text input for text-based questions
    if (
      questionInputType === InterviewQuestionInputType.PHONE ||
      questionInputType === InterviewQuestionInputType.FILE_UPLOAD ||
      questionInputType === InterviewQuestionInputType.TEXT ||
      questionInputType === InterviewQuestionInputType.LONG_TEXT ||
      questionInputType === InterviewQuestionInputType.NUMERICAL
    ) {
      if (questionInputType === InterviewQuestionInputType.FILE_UPLOAD) {
        if (!sessionId || !currentQuestionId) {
          return null;
        }

        return (
          <FileUpload
            disabled={
              isLoading || isBackLoading || isInterviewEnded || !usersTurn
            }
            translate={translate}
            onSubmit={(fileUrl, fileName) =>
              handleSend(fileUrl, {
                displayContent: fileName,
                inputType: InterviewQuestionInputType.FILE_UPLOAD,
              })
            }
            onUploadingChange={setIsFileUploading}
            questionId={currentQuestionId}
            sessionId={sessionId}
          />
        );
      }

      const canSwitchToVoice = getCanSwitchToVoice(
        voiceUrls ?? undefined,
        responseFormat,
      );
      return (
        <Flex direction="column" gap={4} style={{ width: "100%" }}>
          <MessageInput
            key={editTarget ? `edit-${editTarget.resetKey}` : "live-input"}
            onSend={handleSendFireAndForget}
            disabled={
              isLoading || isBackLoading || isInterviewEnded || !usersTurn
            }
            placeholder={translate("Enter message")}
            inputType={questionInputType || InterviewQuestionInputType.TEXT}
            initialValue={
              isEditingPreviousResponse ? editTarget?.content : undefined
            }
            autoFocus={isEditingPreviousResponse}
            translate={translate}
          />
          {canSwitchToVoice && (
            <Flex justify="flex-end">
              <Button
                leftSection={<IconMicrophone size={16} />}
                onClick={() => {
                  track(AnalyticsEvents.TextModeSwitchedToVoice, {
                    currentQuestionId,
                    responseFormat,
                  });
                  setQuestionInputType(InterviewQuestionInputType.VOICE);
                }}
                size="xs"
                variant="subtle"
              >
                {translate("Record instead")}
              </Button>
            </Flex>
          )}
        </Flex>
      );
    }

    return null;
  };

  // Render choice buttons (shown in message area for choice questions)
  const renderChoiceButtons = () => {
    if (!isChoiceEnabled) return null;

    switch (questionInputType) {
      case InterviewQuestionInputType.YES_NO:
        return (
          <YesNo
            key={editTarget ? `edit-${editTarget.resetKey}` : "live-yes-no"}
            onSubmit={handleChoiceSubmit}
            options={questionOptions}
            disabled={isLoading || isBackLoading}
            initialSelectedOptionId={
              isEditingPreviousResponse
                ? editTarget?.selectedOptionIds?.[0]
                : undefined
            }
            translate={translate}
          />
        );

      case InterviewQuestionInputType.SINGLE_CHOICE:
        return (
          <MultipleChoice
            key={editTarget ? `edit-${editTarget.resetKey}` : "live-single"}
            multiSelect={false}
            options={questionOptions}
            onSubmit={handleChoiceSubmit}
            onSelectionChange={handleChoiceSelectionChange}
            disabled={isLoading || isBackLoading}
            initialSelectedOptionIds={
              isEditingPreviousResponse
                ? editTarget?.selectedOptionIds
                : undefined
            }
            translate={translate}
          />
        );

      case InterviewQuestionInputType.MULTIPLE_CHOICE:
        return (
          <MultipleChoice
            key={editTarget ? `edit-${editTarget.resetKey}` : "live-multi"}
            multiSelect={true}
            options={questionOptions}
            onSubmit={handleChoiceSubmit}
            onSelectionChange={handleChoiceSelectionChange}
            disabled={isLoading || isBackLoading}
            initialSelectedOptionIds={
              isEditingPreviousResponse
                ? editTarget?.selectedOptionIds
                : undefined
            }
            translate={translate}
          />
        );

      default:
        return null;
    }
  };

  // A paused location refuses session creation (backend 409). Show the same
  // paused screen the 2.0 flow renders instead of leaving the candidate on an
  // empty, stuck interview screen.
  if (notAcceptingCandidates) {
    return (
      <PausedScreen
        data-testid="interview-not-accepting"
        message={translate(NOT_ACCEPTING_CANDIDATES_MESSAGE)}
      />
    );
  }

  return (
    <Flex
      className={styles.chatContainer}
      direction="column"
      data-testid="chat-container"
    >
      <MessageList
        messages={displayMessages}
        company={company}
        choiceButtons={renderChoiceButtons()}
        translate={translate}
        sessionId={sessionId}
      />

      <Flex className={styles.bottomSection} direction="column" justify="end">
        <Flex align="center" className={styles.inputContainer} justify="center">
          {status === InterviewUIStatus.IN_PROGRESS && renderInputComponent()}
          {status === InterviewUIStatus.IN_PROGRESS &&
            usersTurn &&
            !isLoading &&
            !isBackLoading &&
            !isVoiceUploading &&
            !isFileUploading &&
            canSkip &&
            (questionInputType === InterviewQuestionInputType.VOICE ||
              questionInputType === InterviewQuestionInputType.FILE_UPLOAD) && (
              <Button
                aria-label={translate("Skip question")}
                onClick={() =>
                  handleSendFireAndForget(SKIP_RESPONSE_VALUE, {
                    // AS-1844: classify skipped voice as 'skipped' rather
                    // than falling back to 'written' (the choice/written
                    // inference in useInterview.sendMessage).
                    inputType: questionInputType,
                    responseFormat: "skipped",
                  })
                }
                rightSection={<IconArrowRight size={14} />}
                size="xs"
                variant="subtle"
              >
                {translate("Skip")}
              </Button>
            )}

          {isCompleted && (
            <Flex direction="column" align="center" gap="lg" w="100%">
              <CompletionBadge
                text={translate(
                  "You're all set — your interview has been submitted.",
                )}
              />
              {feedbackSubmitted ? (
                <CompletionBadge text={translate("Feedback submitted")} />
              ) : (
                <InterviewFeedback
                  translate={translate}
                  track={track}
                  onFeedbackSubmitted={() => setFeedbackSubmitted(true)}
                />
              )}
            </Flex>
          )}

          {status === InterviewUIStatus.CANCELLED &&
            cancelReason !== "declined_intro" && (
              <Text ta="center" c="red">
                {translate(
                  "Interview ended unexpectedly. Please contact support if you need assistance.",
                )}
              </Text>
            )}
        </Flex>

        <div
          className={clsx(
            styles.termsContainer,
            isPreviousQuestionVisible && styles.termsContainerWithBack,
          )}
        >
          {isPreviousQuestionVisible && (
            <button
              aria-disabled={isPreviousQuestionDisabled}
              className={styles.previousQuestionButton}
              disabled={isPreviousQuestionDisabled}
              onClick={handleBackToPreviousQuestion}
              type="button"
            >
              <IconArrowLeft size={14} stroke={2} />
              <span>{translate("Back to previous question")}</span>
            </button>
          )}
          <p className={styles.terms}>
            {translate("Powered by Upwage")} |{" "}
            <a
              className={styles.termsLink}
              href="https://www.upwage.com/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate("Terms")}
            </a>
          </p>
        </div>
      </Flex>
    </Flex>
  );
}
